/**
 * =============================================================================
 * LA LISTA DE TESTS DE UN PACIENTE
 * =============================================================================
 * ANTES ERA UNA HILERA DE BOTONES IGUALES y se veia desordenada. No era solo
 * estetica: eran tres cosas a la vez, y la primera era un fallo de verdad.
 *
 *   1. El orden no estaba definido. El endpoint agrupaba por evaluacion y no
 *      volvia a ordenar, y el orden de salida de un `$group` de Mongo no esta
 *      garantizado: los tests podian cambiar de sitio entre dos cargas de los
 *      mismos datos. Corregido en el servidor.
 *
 *   2. Anchos de 3 a 12 palabras envueltos con `flex-wrap`. Con titulos tan
 *      dispares —"MEC de Lobo (Mini-Examen Cognoscitivo)" al lado de "STAI —
 *      Cuestionario de Ansiedad Estado-Rasgo (versión abreviada, 13 ítems
 *      SI/NO)"— las filas salen dentadas por definicion. No hay CSS que lo
 *      arregle manteniendo ese contenido: hay que cambiar la forma.
 *
 *   3. Todos identicos. Mismo color, mismo peso, misma forma. Nada que permita
 *      barrer con la vista.
 *
 * AHORA SON FILAS DE ANCHO COMPLETO AGRUPADAS POR AREA CLINICA. Alineadas por
 * construccion —una rejilla, no un envoltorio— y con lo que el terapeuta viene
 * a saber: cuanto salio la ultima vez, que significa, cuantas veces se ha
 * medido y cuanto hace.
 *
 * Y NO HIZO FALTA TOCAR EL BACKEND PARA ESO. El endpoint ya devolvia
 * `clinicalArea`, `lastDate`, `lastProcessedScore` y `subtype`; la interfaz
 * usaba dos campos de siete y tiraba el resto.
 * =============================================================================
 */

import { useMemo } from "react";
import { Segmented, Empty, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { opcionesDePeriodo } from "./chartConstants";
import {
  agruparPorArea,
  partirTitulo,
  puntuacionDeUltima,
} from "./resumenDeEvaluaciones";
import "./EvaluationPicker.scss";

/**
 * El area viene de la base de datos escrita en castellano. Se traduce cuando la
 * conocemos y se pinta en crudo cuando no: un area nueva creada desde el panel
 * tiene que salir con su nombre, no con una clave ni desaparecida.
 */
function nombreDeArea(area, clave, t) {
  if (!area) return t("charts.picker.areas.sinArea");
  const traducida = t(`charts.picker.areas.${clave}`, { defaultValue: "" });
  return traducida || area;
}

export function EvaluationPicker({
  evaluationSummary = [],
  selectedEvaluationId,
  onSelect,
  timeRange,
  onTimeRangeChange,
  loading = false,
}) {
  const { t } = useTranslation("motricity");
  const { fechaRelativa } = useFormatos();

  // Agrupar y ordenar es caro comparado con pintar, y el resumen solo cambia
  // al cambiar de paciente o de periodo.
  const grupos = useMemo(
    () => agruparPorArea(evaluationSummary),
    [evaluationSummary],
  );

  if (loading) return <Spin />;

  return (
    <div className="revi-eval-picker">
      <Segmented
        value={timeRange}
        onChange={onTimeRangeChange}
        options={opcionesDePeriodo(t)}
      />

      {grupos.length === 0 ? (
        <div className="revi-eval-picker__empty">
          <Empty
            description={t("charts.picker.empty")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="revi-eval-picker__areas">
          {grupos.map((g) => (
            <section
              key={g.clave || "sin-area"}
              className="revi-eval-picker__area"
            >
              <h4 className="revi-eval-picker__area-titulo">
                {nombreDeArea(g.area, g.clave, t)}
                <span className="revi-eval-picker__area-cuenta">
                  {g.tests.length}
                </span>
              </h4>

              <ul className="revi-eval-picker__lista">
                {g.tests.map((ev) => {
                  const activo = selectedEvaluationId === ev.evaluationId;
                  // El titulo lo escribio el terapeuta: es contenido clinico
                  // suyo y no se traduce. Lo que hacemos es darle jerarquia.
                  const { principal, matiz } = partirTitulo(ev.title);
                  const punt = puntuacionDeUltima(ev.lastProcessedScore);

                  return (
                    <li key={ev.evaluationId}>
                      <button
                        type="button"
                        aria-pressed={activo}
                        className={`revi-eval-picker__fila${activo ? " revi-eval-picker__fila--activa" : ""}`}
                        onClick={() => onSelect(ev.evaluationId)}
                      >
                        <span className="revi-eval-picker__nombre">
                          <span className="revi-eval-picker__titulo">
                            {principal}
                          </span>
                          {matiz ? (
                            <span className="revi-eval-picker__matiz">
                              {matiz}
                            </span>
                          ) : null}
                        </span>

                        <span className="revi-eval-picker__valor">
                          {punt ? (
                            <>
                              <span className="revi-eval-picker__total">
                                {punt.total}
                              </span>
                              {/* El maximo solo si lo hay. Un "12/" cortado es
                                  peor que un "12" a secas. */}
                              {punt.max !== null ? (
                                <span className="revi-eval-picker__max">
                                  {`/${punt.max}`}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="revi-eval-picker__sin-dato">
                              —
                            </span>
                          )}
                        </span>

                        <span className="revi-eval-picker__interpretacion">
                          {punt?.interpretacion || ""}
                        </span>

                        <span className="revi-eval-picker__meta">
                          {t("charts.picker.mediciones", { count: ev.count })}
                          {ev.lastDate ? (
                            <>
                              <span className="revi-eval-picker__sep">·</span>
                              {fechaRelativa(ev.lastDate)}
                            </>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
